import axiosInstance from './axiosCreator';
import {
  Subscription,
  SubscriptionPlan,
  SubscriptionAccess,
  AnnualBilling,
  // SubscriptionStatus,
} from '../types/Subscription';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { setSubscription } from '../slices/subscriptions/subscriptionsSlice';
import { store } from '../store/store';
import axios from 'axios';

// Add this interface definition
export interface Invoice {
  id: string;
  number: string;
  created: number;
  period_start: number;
  period_end: number;
  amount_due: number;
  amount_paid: number;
  status: string;
  hosted_invoice_url?: string;
  invoice_pdf?: string;
  lines?: {
    data: Array<{
      description: string;
      amount: number;
    }>;
  };
}

/**
 * Get the current subscription status for the logged-in user.
 * Includes details such as trial end, subscription end, plan, and access flag.
 */
export const getSubscriptionStatus = async (): Promise<Subscription | void> => {
  try {
    const response = await axiosInstance.get('/subscription/status');
    if (response.data) {
      store.dispatch(setSubscription(response.data));
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching subscription status:', error);
  }
};

/**
 * Creates a checkout session for the selected subscription plan.
 * The backend checks if a Stripe customer exists (or creates one) and returns a checkout session URL.
 */
export const createCheckoutSession = async (
  product: SubscriptionAccess,
  plan: SubscriptionPlan,
  annualBilling?: AnnualBilling,
  isMobileCheckout?: boolean,
  skipAutoDiscount?: boolean,
  promotionCode?: string
): Promise<{ url: string } | void> => {
  try {
    const body: Record<string, unknown> = {
      product,
      plan,
    };

    if (plan === SubscriptionPlan.ANNUAL && annualBilling) {
      body.annualBilling = annualBilling;
    }
    if (isMobileCheckout) {
      body.isMobileCheckout = true;
    }
    if (skipAutoDiscount) {
      body.skipAutoDiscount = true;
    }
    if (promotionCode) {
      body.promotionCode = promotionCode;
    }
    const response = await axiosInstance.post('/subscription/checkout', body);
    return response.data;
  } catch (error) {
    if (promotionCode) {
      throw new Error(
        'We could not apply that promo code at checkout. Please try again.'
      );
    }
    console.error('Error creating checkout session:', error);
  }
};

// The default one-time access SKU (5-year "Student Life"). Must match the
// backend `OneTimeAccessSku` catalog (ora-be `stripeUtils.ts`).
export const ONE_TIME_ACCESS_SKU_FIVE_YEAR = 'STEP_SHELF_FIVE_YEAR';

// Buy-intent marker persisted through signup so the one-time checkout resumes
// post-auth (Onboarding). Must match the backend `BuyIntent.FIVE_YEAR` enum.
export const FIVE_YEAR_BUY_INTENT = 'FIVE_YEAR';

/**
 * Creates a Stripe checkout session for a one-time (non-recurring) access
 * purchase, e.g. the 5-year option. Backend uses Stripe `mode: 'payment'` and
 * fulfills an AccessPurchase on completion.
 */
export const createOneTimeCheckoutSession = async (
  sku: string = ONE_TIME_ACCESS_SKU_FIVE_YEAR,
  isMobileCheckout?: boolean,
  promotionCode?: string
): Promise<{ url: string } | void> => {
  try {
    const body: Record<string, unknown> = { sku };
    if (isMobileCheckout) {
      body.isMobileCheckout = true;
    }
    if (promotionCode) {
      body.promotionCode = promotionCode;
    }

    const response = await axiosInstance.post(
      '/subscription/checkout-one-time',
      body
    );

    return response.data;
  } catch (error) {
    if (promotionCode) {
      throw new Error(
        'We could not apply that promo code at checkout. Please try again.'
      );
    }
    console.error('Error creating one-time checkout session:', error);
  }
};

/**
 * Initiates a one-time access checkout and redirects to the Stripe URL.
 * Mirrors `handleCheckout` for the recurring plans.
 */
export const handleOneTimeCheckout = async (
  sku: string = ONE_TIME_ACCESS_SKU_FIVE_YEAR,
  isMobileCheckout?: boolean,
  promotionCode?: string
) => {
  try {
    const session = await createOneTimeCheckoutSession(
      sku,
      isMobileCheckout ? true : undefined,
      promotionCode
    );

    if (session?.url) {
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: session.url });
      } else {
        window.location.href = session.url;
      }
    } else {
      if (promotionCode) {
        throw new Error(
          'We could not apply that promo code at checkout. Please try again.'
        );
      }
      console.error('Failed to create one-time checkout session');
    }
  } catch (error) {
    if (promotionCode) throw error;
    console.error('Error initiating one-time checkout:', error);
  }
};

/**
 * For testing: Checkout Button Handler
 * Calls the createCheckoutSession endpoint with a dummy plan (here, "MONTHLY")
 * and if successful, redirects the user to the returned checkout session URL.
 */
export const handleCheckout = async (
  product: SubscriptionAccess,
  plan: SubscriptionPlan,
  annualBilling?: AnnualBilling,
  isMobileCheckout?: boolean,
  skipAutoDiscount?: boolean,
  promotionCode?: string
) => {
  try {
    const session = await createCheckoutSession(
      product,
      plan,
      annualBilling,
      isMobileCheckout ? true : undefined,
      skipAutoDiscount ? true : undefined,
      promotionCode
    );

    if (session?.url) {
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: session.url });
      } else {
        window.location.href = session.url;
      }
    } else {
      if (promotionCode) {
        throw new Error(
          'We could not apply that promo code at checkout. Please try again.'
        );
      }
      console.error('Failed to create checkout session');
    }
  } catch (error) {
    if (promotionCode) throw error;
    console.error('Error initiating checkout:', error);
  }
};

/**
 * Cancels the user's current subscription.
 * The subscription will remain active until the end of the current billing period.
 */
export const cancelSubscription = async (
  reason: string | null
): Promise<void> => {
  try {
    await axiosInstance.post('/subscription/cancel', { reason });
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    throw error;
  }
};

/**
 * Schedules a pause for the current subscription and updates the Redux
 * subscription state from the server's full status response.
 */
export const pauseSubscription = async (
  durationMonths: 1 | 2 | 3
): Promise<Subscription> => {
  try {
    const response = await axiosInstance.post<Subscription>(
      '/subscription/pause',
      { durationMonths }
    );

    store.dispatch(setSubscription(response.data));
    return response.data;
  } catch (error) {
    console.error('Error pausing subscription:', error);
    throw error;
  }
};

/**
 * Resumes a paused subscription immediately and updates the Redux
 * subscription state from the server's full status response.
 */
export const resumeSubscriptionNow = async (): Promise<Subscription> => {
  try {
    const response = await axiosInstance.post<Subscription>(
      '/subscription/resume'
    );

    store.dispatch(setSubscription(response.data));
    return response.data;
  } catch (error) {
    console.error('Error resuming subscription:', error);
    throw error;
  }
};

/**
 * Reactivates a previously canceled subscription.
 * Only works if the subscription is still within its active period.
 */
export const reactivateSubscription = async (): Promise<void> => {
  try {
    await axiosInstance.post('/subscription/reactivate');
  } catch (error) {
    console.error('Error reactivating subscription:', error);
    throw error;
  }
};

/**
 * Retrieves the customer's billing details, payment methods, and subscription information.
 */
export const getBillingInformation = async () => {
  try {
    const response = await axiosInstance.get('/subscription/billing');
    return response.data;
  } catch (error) {
    console.error('Error fetching billing information:', error);
    throw error;
  }
};

/**
 * Updates the customer's billing details.
 * @param billingDetails - Object containing billing information to update (all fields optional)
 */
export const updateBillingDetails = async (billingDetails: {
  name?: string;
  email?: string;
  phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
  tax_id?: string;
}) => {
  try {
    const response = await axiosInstance.put(
      '/subscription/billing',
      billingDetails
    );

    return response.data;
  } catch (error) {
    console.error('Error updating billing details:', error);
    throw error;
  }
};

/**
 * Adds a new payment method to the customer's account.
 * @param paymentMethodId - The payment method ID obtained from Stripe.js
 */
export const addPaymentMethod = async (paymentMethodId: string) => {
  try {
    const response = await axiosInstance.post('/subscription/payment-methods', {
      paymentMethodId,
    });

    return response.data;
  } catch (error) {
    console.error('Error adding payment method:', error);
    throw error;
  }
};

/**
 * Removes a payment method from the customer's account.
 * @param paymentMethodId - The ID of the payment method to remove
 */
export const removePaymentMethod = async (paymentMethodId: string) => {
  try {
    const response = await axiosInstance.delete(
      `/subscription/payment-methods/${paymentMethodId}`
    );

    return response.data;
  } catch (error) {
    console.error('Error removing payment method:', error);
    throw error;
  }
};

/**
 * Sets a payment method as the default for the customer.
 * @param paymentMethodId - The ID of the payment method to set as default
 */
export const setDefaultPaymentMethod = async (paymentMethodId: string) => {
  try {
    const response = await axiosInstance.put(
      `/subscription/payment-methods/${paymentMethodId}/default`
    );

    return response.data;
  } catch (error) {
    console.error('Error setting default payment method:', error);
    throw error;
  }
};

/**
 * Retrieves all invoices for the customer.
 */
export const getInvoices = async () => {
  try {
    const response = await axiosInstance.get('/subscription/invoices');
    return response.data;
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }
};

/**
 * Retrieves a specific invoice by ID.
 * @param invoiceId - The ID of the invoice to retrieve
 */
export const getInvoiceById = async (invoiceId: string) => {
  try {
    const response = await axiosInstance.get(
      `/subscription/invoices/${invoiceId}`
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching invoice:', error);
    throw error;
  }
};

interface Discount {
  name: string;
  code: string;
  percentOff: number | null;
  amountOff: number | null;
  expiresAt: string | null;
  durationType: string;
  durationMonths: number | null;
}

interface DiscountSource {
  name: string;
  scope: string;
  // Headline percent for badge / banner copy (ORA-44). Approximate marketing
  // figure, decoupled from the per-plan coupons' exact amounts.
  displayPercentOff?: number | null;
  // Stable per-occurrence id from the authed /subscription/discount response,
  // used to key per-instance banner dismissals (ORA-188). Absent on the public
  // (logged-out) discount endpoints, which don't drive the dismissible banner.
  instanceId?: string | null;
}

// The plans a per-plan discount can target (ORA-44). Keys match the backend.
export type DiscountPlanKey = 'MONTHLY' | 'ANNUAL' | 'FIVE_YEAR';
export type PromotionCodeDurationType = 'once' | 'repeating' | 'forever';

export interface PromotionCodePlanPreview {
  eligible: boolean;
  reason: string | null;
  originalAmountMinor: number;
  discountedAmountMinor: number | null;
  currency: string;
  promotionCodeId: string | null;
  couponId: string | null;
  percentOff: number | null;
  amountOffMinor: number | null;
  durationType: PromotionCodeDurationType | null;
  durationMonths: number | null;
}

export interface PromotionCodePreview {
  code: string;
  normalizedCode: string;
  plans: Record<DiscountPlanKey, PromotionCodePlanPreview>;
}

/**
 * Validates a customer-facing Stripe promotion code and previews its exact
 * price for every plan. Errors are intentionally rethrown with safe copy so
 * the pricing UI can preserve automatic discounts and explain what happened.
 */
export const previewPromotionCode = async (
  promotionCode: string
): Promise<PromotionCodePreview> => {
  try {
    const response = await axiosInstance.post<PromotionCodePreview>(
      '/subscription/promotion-code/preview',
      { promotionCode }
    );

    return response.data;
  } catch (error) {
    const backendMessage = axios.isAxiosError<{
      error?: unknown;
      message?: unknown;
    }>(error)
      ? (error.response?.data?.error ?? error.response?.data?.message)
      : null;

    throw new Error(
      typeof backendMessage === 'string' && backendMessage.length <= 240
        ? backendMessage
        : 'We could not validate that promo code. Please check it and try again.'
    );
  }
};

export interface PlanDiscount {
  name: string;
  percentOff: number | null;
  amountOff: number | null; // dollars
  displayPercentOff: number | null;
  durationType: string | null;
  durationMonths: number | null;
  expiresAt: string | null;
  // Optional per-plan trial override for partner offers. Absent means the
  // standard trial applies.
  trialPeriodDays?: number | null;
}

export interface DiscountResponse {
  hasDiscount: boolean;
  discount: Discount | null;
  source: DiscountSource | null;
  // Per-plan discounts (ORA-44). Present when the discount varies by plan; each
  // plan the page renders looks up its own entry. Older single-discount
  // responses omit this and fall back to `discount`.
  plans?: Partial<Record<DiscountPlanKey, PlanDiscount | null>>;
  // Whether checkout would actually grant the offer's trial to this user. Set
  // by the authed fetch from the same predicate checkout uses; absent on
  // public/unauthenticated fetches and on responses persisted before the
  // backend supplied it, so absent ⇒ treat as eligible. An explicit `false`
  // (a live billing relationship or elevated access) suppresses the
  // trial-forward messaging on the Pricing page.
  isTrialEligible?: boolean;
}

export const fetchActiveDiscount =
  async (): Promise<DiscountResponse | void> => {
    const response = await axiosInstance.get('/subscription/discount');
    return response.data;
  };

// Universal ("summer sale") per-plan discount for logged-out visitors (ORA-44).
// Returns a DiscountResponse whose `plans` drives the per-plan pricing display.
export const fetchPublicUniversalDiscount =
  async (): Promise<DiscountResponse | null> => {
    try {
      const response = await axiosInstance.get('/public/universal-discount');
      const data = response.data;
      if (!data?.hasDiscount || !data?.plans) return null;

      return {
        hasDiscount: true,
        discount: null,
        source: data.source
          ? {
              name: data.source.name ?? '',
              scope: 'segment',
              displayPercentOff: data.source.displayPercentOff ?? null,
            }
          : null,
        plans: data.plans,
      };
    } catch (error) {
      console.error('Error fetching public universal discount:', error);
      return null;
    }
  };

export const fetchPublicCohortDiscount = async (
  cohort: string
): Promise<DiscountResponse | null> => {
  if (!cohort.trim()) return null;

  try {
    const response = await axiosInstance.get(
      `/public/cohort-discount/${encodeURIComponent(cohort)}`
    );

    const data = response.data;

    if (data?.hasDiscount && data?.discount) {
      return {
        hasDiscount: true,
        discount: {
          name: '',
          code: '',
          percentOff: data.discount.percentOff ?? null,
          amountOff: data.discount.amountOff ?? null,
          expiresAt: data.discount.expiresAt ?? null,
          durationType: '',
          durationMonths: data.discount.durationMonths ?? null,
        },
        source: data.source
          ? {
              name: data.source.name ?? '',
              scope: data.source.scope ?? 'cohort',
              displayPercentOff: data.source.displayPercentOff ?? null,
            }
          : null,
        // Per-plan coupons (School Launch: $30/$300/$600 off) — drives the
        // correct per-plan price so annual/5-yr aren't given the monthly $30
        // off. Absent from older backends; getPlanDiscount falls back to the
        // single top-level discount.
        plans: data.plans ?? undefined,
      };
    }

    return null;
  } catch (error) {
    console.error('Error fetching public cohort discount:', error);
    return null;
  }
};

export const fetchPublicMarketingOfferDiscount = async (
  marketingOfferKey: string
): Promise<DiscountResponse | null> => {
  if (!marketingOfferKey.trim()) return null;

  try {
    const response = await axiosInstance.get(
      `/public/marketing-offer-discount/${encodeURIComponent(marketingOfferKey)}`
    );

    const data = response.data;

    if (data?.hasDiscount && data?.discount) {
      return {
        hasDiscount: true,
        discount: {
          name: '',
          code: '',
          percentOff: data.discount.percentOff ?? null,
          amountOff: data.discount.amountOff ?? null,
          expiresAt: data.discount.expiresAt ?? null,
          durationType: '',
          durationMonths: data.discount.durationMonths ?? null,
        },
        // Preserve the source so the banner shows the headline percent
        // ("… — 50% off Ora Pro!") instead of generic "free month" copy.
        source: data.source
          ? {
              name: data.source.name ?? '',
              scope: data.source.scope ?? 'marketing_offer',
              displayPercentOff: data.source.displayPercentOff ?? null,
            }
          : null,
        // Per-plan coupons ($30/$300/$600 off) — drives the correct per-plan
        // price so annual/5-yr aren't given the monthly $30 off.
        plans: data.plans ?? undefined,
      };
    }

    return null;
  } catch (error) {
    console.error('Error fetching public marketing offer discount:', error);
    return null;
  }
};
